import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector, RootState } from '../../store';
import {
  createInvoice,
  updateInvoice,
  fetchUnbilledExpenses,
  fetchBillingRates,
  resolveBillingRate,
} from '../../store/actions/FinanceActions';
import { listClients } from '../../store/actions/ClientActions';
import { fetchMatters } from '../../store/actions/Matters/MatterActions';
import Button from '../shared/TailwindComponents/Button';
import Input from '../shared/TailwindComponents/Input';
import Textarea from '../shared/TailwindComponents/Textarea';
import Dropdown from '../shared/TailwindComponents/Dropdown';
import Badge from '../shared/TailwindComponents/Badge';
import Toggle from '../shared/TailwindComponents/Toggle';
import CheckBox from '../shared/TailwindComponents/CheckBox';
import { FormSection, FormActions } from '../shared/FormComponents';
import { Well } from '../shared/brand/Field';
import {
  PiXLight,
  PiPlusLight,
  PiTrashLight,
  PiReceiptLight,
  PiArrowLeftLight,
  PiArrowRightLight,
  PiDownloadLight,
  PiCheckLight,
  PiUserLight,
  PiListLight,
  PiWarningLight,
} from 'react-icons/pi';
import { FinanceExpense, FinanceBillingRate, FinanceInvoice } from '../../store/reducer/FinanceReducer';
import { useOrgMembers } from '../../hooks/useOrgMembers';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onCreated: () => void;
  organizationId: number;
  editInvoice?: FinanceInvoice | null;
}

interface LineItem {
  description: string;
  amount: string;
  quantity: string;
  assigneeId?: string;
  assigneeName?: string;
  rateId?: number;
}

const STEPS = ['client', 'items', 'adjustments', 'preview'] as const;
type Step = typeof STEPS[number];

const EXPENSE_CATEGORIES: Record<string, string> = {
  filing_fee: 'Filing Fee',
  court_cost: 'Court Cost',
  travel: 'Travel',
  expert_witness: 'Expert Witness',
  copying: 'Copying',
  postage: 'Postage',
  research: 'Research',
  other: 'Other',
};

const formatCurrency = (amount: number, currency: string = 'usd') => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency.toUpperCase(),
    minimumFractionDigits: 2,
  }).format(amount / 100);
};

const CreateInvoiceModal: React.FC<Props> = ({ isOpen, onClose, onCreated, organizationId, editInvoice }) => {
  const isEdit = !!editInvoice;
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const loading = useAppSelector((state: RootState) => state.finance.loading);
  const unbilledExpenses = useAppSelector((state: RootState) => state.finance.unbilledExpenses);
  const billingRates = useAppSelector((state: RootState) => state.finance.billingRates);
  const matters = useAppSelector((state: RootState) => state.matters?.matters || []);
  const preferredCurrency = useAppSelector((state: RootState) => state.user?.userInfo?.preferences?.general_settings?.currency) || 'usd';
  const { members: orgMembers } = useOrgMembers();

  const [step, setStep] = useState<Step>('client');
  const [clients, setClients] = useState<any[]>([]);
  const [showRatesPicker, setShowRatesPicker] = useState(false);
  const [showDiscardWarning, setShowDiscardWarning] = useState(false);

  // Step 1: Client & Matter
  const [clientId, setClientId] = useState<number | null>(null);
  const [matterId, setMatterId] = useState<number | null>(null);
  const [clientName, setClientName] = useState('');
  const [clientEmail, setClientEmail] = useState('');

  // Step 2: Line Items
  const [lineItems, setLineItems] = useState<LineItem[]>([
    { description: '', amount: '', quantity: '1' },
  ]);
  const [selectedExpenseIds, setSelectedExpenseIds] = useState<number[]>([]);
  const [showExpenseImport, setShowExpenseImport] = useState(false);

  // Step 3: Adjustments
  const [taxRate, setTaxRate] = useState('');
  const [discountRate, setDiscountRate] = useState('');
  const [dueDays, setDueDays] = useState('30');
  const [paymentTerms, setPaymentTerms] = useState('');
  const [notes, setNotes] = useState('');
  const [internalMemo, setInternalMemo] = useState('');
  const [currency, setCurrency] = useState(preferredCurrency || 'usd');
  const [isRecurring, setIsRecurring] = useState(false);
  const [recurringInterval, setRecurringInterval] = useState('');
  const [billingAddress, setBillingAddress] = useState({
    line1: '', line2: '', city: '', state: '', postal_code: '', country: '',
  });

  // Pre-populate form when editing
  useEffect(() => {
    if (isOpen && editInvoice) {
      setClientId(editInvoice.client_id || null);
      setMatterId(editInvoice.matter_id || null);
      setClientName(editInvoice.client_name || '');
      setClientEmail(editInvoice.client_email || '');
      setCurrency(editInvoice.currency || preferredCurrency || 'usd');
      setNotes(editInvoice.notes || '');
      setInternalMemo(editInvoice.internal_memo || '');
      setPaymentTerms(editInvoice.payment_terms || '');
      setIsRecurring(editInvoice.is_recurring || false);
      setRecurringInterval(editInvoice.recurring_interval || '');
      setTaxRate(editInvoice.tax_rate ? String(editInvoice.tax_rate * 100) : '');
      setDiscountRate(editInvoice.discount_rate ? String(editInvoice.discount_rate * 100) : '');

      // Parse due days from payment_terms "Net X" or from due_date
      if (editInvoice.payment_terms?.match(/Net\s*(\d+)/i)) {
        setDueDays(editInvoice.payment_terms.match(/Net\s*(\d+)/i)![1]);
      }

      // Billing address
      if (editInvoice.billing_address && typeof editInvoice.billing_address === 'object') {
        setBillingAddress({
          line1: editInvoice.billing_address.line1 || '',
          line2: editInvoice.billing_address.line2 || '',
          city: editInvoice.billing_address.city || '',
          state: editInvoice.billing_address.state || '',
          postal_code: editInvoice.billing_address.postal_code || '',
          country: editInvoice.billing_address.country || '',
        });
      }

      // Line items - amounts stored in cents, convert back
      if (editInvoice.line_items?.length) {
        setLineItems(
          editInvoice.line_items.map((li: any) => ({
            description: li.description || '',
            amount: li.amount ? String(li.amount / 100) : '',
            quantity: String(li.quantity || 1),
            assigneeId: li.assigneeId || undefined,
            assigneeName: li.assigneeName || undefined,
            rateId: li.rateId || undefined,
          }))
        );
      }

      setStep('items'); // skip client step, go straight to items
    }
  }, [isOpen, editInvoice]);

  // Load clients, billing rates on open
  useEffect(() => {
    if (isOpen) {
      listClients(1, 200).then((res) => {
        if (res?.clients) setClients(res.clients);
      });
      dispatch(fetchMatters({ page: 1, page_size: 200 }));
      if (organizationId) {
        dispatch(fetchBillingRates(organizationId));
      }
    }
  }, [isOpen, dispatch, organizationId]);

  // Load unbilled expenses when client/matter changes
  useEffect(() => {
    if (isOpen && organizationId) {
      dispatch(fetchUnbilledExpenses(organizationId, clientId || undefined, matterId || undefined));
    }
  }, [isOpen, organizationId, clientId, matterId, dispatch]);

  // Auto-fill client info
  useEffect(() => {
    if (clientId) {
      const client = clients.find((c: any) => (c.id || c.ClientID) == clientId);
      if (client) {
        setClientName(client.name || client.Name || '');
        setClientEmail(client.contact_email || client.Email || '');
      }
    } else {
      setClientName('');
      setClientEmail('');
    }
  }, [clientId, clients]);

  const clientOptions = useMemo(() =>
    clients.map((c: any) => ({
      value: String(c.id || c.ClientID),
      label: c.name || c.Name || t('Unnamed Client'),
    })),
    [clients]
  );

  const matterOptions = useMemo(() => {
    if (!clientId) {
      return matters.map((m: any) => ({
        value: String(m.id || m.MatterID),
        label: m.title || m.Title || t('Untitled Matter'),
      }));
    }

    // Find selected client to get their UUID for matter_clients matching
    const selectedClient = clients.find((c: any) => String(c.id || c.ClientID) === String(clientId));
    const selectedClientUuid = selectedClient?.uuid || selectedClient?.client_uuid;
    const selectedClientName = selectedClient?.name || selectedClient?.Name;

    const filtered = matters.filter((m: any) => {
      // 1. Check direct client_id (legacy field)
      const mClientId = m.clientId || m.client_id || m.ClientID;
      if (mClientId != null && String(mClientId) === String(clientId)) return true;

      // 2. Check matter_clients relationship (current approach)
      const mClients = m.matter_clients?.clients || (Array.isArray(m.matter_clients) ? m.matter_clients : []);
      if (mClients.length > 0) {
        return mClients.some((mc: any) => {
          if (selectedClientUuid && mc.client_uuid) return mc.client_uuid === selectedClientUuid;
          if (selectedClientName && mc.name) return mc.name === selectedClientName;
          return false;
        });
      }

      // 3. Check Clients array (PascalCase legacy)
      if (Array.isArray(m.Clients)) {
        return m.Clients.some((c: any) => String(c.id || c.ClientID) === String(clientId));
      }

      return false;
    });

    return filtered.map((m: any) => ({
      value: String(m.id || m.MatterID),
      label: m.title || m.Title || 'Untitled Matter',
    }));
  }, [matters, clientId, clients]);

  // ── Line Item Management ──────────────────────────────────

  const addLineItem = () => {
    setLineItems([...lineItems, { description: '', amount: '', quantity: '1' }]);
  };

  const removeLineItem = (index: number) => {
    if (lineItems.length > 1) {
      setLineItems(lineItems.filter((_, i) => i !== index));
    }
  };

  const updateLineItem = (index: number, field: keyof LineItem, value: string) => {
    const updated = [...lineItems];
    updated[index] = { ...updated[index], [field]: value };
    setLineItems(updated);
  };

  /** Assign a team member to a line item and auto-resolve their billing rate */
  const assignTeamMember = async (index: number, memberId: string) => {
    const member = orgMembers.find(m => m.id === memberId);
    const updated = [...lineItems];
    updated[index] = {
      ...updated[index],
      assigneeId: memberId || undefined,
      assigneeName: member?.name || undefined,
    };
    setLineItems(updated);

    // Auto-resolve billing rate for this member
    if (memberId && organizationId) {
      const rate = await resolveBillingRate({
        organization_id: organizationId,
        user_id: parseInt(memberId),
        client_id: clientId || undefined,
        matter_id: matterId || undefined,
      });
      if (rate) {
        const rateAmount = rate.rate_type === 'contingency'
          ? (rate.amount / 100).toFixed(1)
          : (rate.amount / 100).toFixed(2);
        const updatedWithRate = [...lineItems];
        updatedWithRate[index] = {
          ...updatedWithRate[index],
          assigneeId: memberId,
          assigneeName: member?.name || undefined,
          amount: rateAmount,
          rateId: rate.id,
          description: updatedWithRate[index].description || (rate.description || `${member?.name || ''} - ${rate.rate_type}`),
        };
        setLineItems(updatedWithRate);
      }
    }
  };

  /** Add a line item pre-populated from an existing billing rate / service */
  const addFromRate = (rate: FinanceBillingRate) => {
    const rateAmount = rate.rate_type === 'contingency'
      ? (rate.amount / 100).toFixed(1)
      : (rate.amount / 100).toFixed(2);
    const newItem: LineItem = {
      description: rate.description || `${rate.rate_type} rate`,
      amount: rateAmount,
      quantity: '1',
      rateId: rate.id,
    };
    setLineItems([...lineItems.filter(li => li.description || li.amount), newItem]);
    setShowRatesPicker(false);
  };

  /** Add a team member as a new line item with their resolved rate */
  const addTeamMemberItem = async (memberId: string) => {
    const member = orgMembers.find(m => m.id === memberId);
    if (!member) return;

    const newItem: LineItem = {
      description: `${member.name}`,
      amount: '',
      quantity: '1',
      assigneeId: memberId,
      assigneeName: member.name,
    };

    if (organizationId) {
      const rate = await resolveBillingRate({
        organization_id: organizationId,
        user_id: parseInt(memberId),
        client_id: clientId || undefined,
        matter_id: matterId || undefined,
      });
      if (rate) {
        newItem.amount = rate.rate_type === 'contingency'
          ? (rate.amount / 100).toFixed(1)
          : (rate.amount / 100).toFixed(2);
        newItem.rateId = rate.id;
        newItem.description = rate.description || `${member.name} - ${rate.rate_type}`;
      }
    }

    setLineItems([...lineItems.filter(li => li.description || li.amount), newItem]);
  };

  const importExpenses = () => {
    const newItems: LineItem[] = unbilledExpenses
      .filter((e: FinanceExpense) => selectedExpenseIds.includes(e.id))
      .map((e: FinanceExpense) => ({
        description: `${EXPENSE_CATEGORIES[e.category] || e.category}: ${e.description || ''}`.trim(),
        amount: String(e.amount / 100),
        quantity: '1',
      }));
    setLineItems([...lineItems.filter(li => li.description || li.amount), ...newItems]);
    setShowExpenseImport(false);
  };

  const toggleExpense = (id: number) => {
    setSelectedExpenseIds((prev) =>
      prev.includes(id) ? prev.filter((eid) => eid !== id) : [...prev, id]
    );
  };

  // ── Calculations ──────────────────────────────────────────

  const subtotal = useMemo(() => {
    return lineItems.reduce((sum, item) => {
      const amount = parseFloat(item.amount) || 0;
      const qty = parseInt(item.quantity) || 1;
      return sum + amount * qty * 100;
    }, 0);
  }, [lineItems]);

  const taxAmount = useMemo(() => {
    const rate = parseFloat(taxRate) || 0;
    return Math.round(subtotal * (rate / 100));
  }, [subtotal, taxRate]);

  const discountAmount = useMemo(() => {
    const rate = parseFloat(discountRate) || 0;
    return Math.round(subtotal * (rate / 100));
  }, [subtotal, discountRate]);

  const totalAmount = subtotal + taxAmount - discountAmount;

  // ── Navigation ────────────────────────────────────────────

  const stepIndex = STEPS.indexOf(step);
  const canGoNext = () => {
    if (step === 'items') {
      return lineItems.some((li) => li.description && li.amount);
    }
    return true;
  };

  const goNext = () => {
    const idx = STEPS.indexOf(step);
    if (idx < STEPS.length - 1) setStep(STEPS[idx + 1]);
  };

  const goBack = () => {
    const idx = STEPS.indexOf(step);
    if (idx > 0) setStep(STEPS[idx - 1]);
  };

  // ── Submit ────────────────────────────────────────────────

  const handleSubmit = async () => {
    const items = lineItems
      .filter((item) => item.description && item.amount)
      .map((item) => ({
        description: item.description,
        amount: Math.round(parseFloat(item.amount) * 100),
        quantity: parseInt(item.quantity) || 1,
      }));

    if (items.length === 0) return;

    const hasAddress = Object.values(billingAddress).some((v) => v.trim());

    const payload = {
      organization_id: organizationId,
      client_id: clientId || undefined,
      matter_id: matterId || undefined,
      line_items: items,
      currency,
      due_days: parseInt(dueDays) || 30,
      notes: notes || undefined,
      tax_rate: taxRate ? parseFloat(taxRate) / 100 : undefined,
      discount_rate: discountRate ? parseFloat(discountRate) / 100 : undefined,
      internal_memo: internalMemo || undefined,
      billing_address: hasAddress ? billingAddress : undefined,
      payment_terms: paymentTerms || undefined,
      is_recurring: isRecurring,
      recurring_interval: recurringInterval || undefined,
      expense_ids: selectedExpenseIds.length > 0 ? selectedExpenseIds : undefined,
    };

    const result = isEdit && editInvoice
      ? await dispatch(updateInvoice(editInvoice.id, payload))
      : await dispatch(createInvoice(payload));

    if (result) {
      onCreated();
      resetForm();
    }
  };

  const resetForm = () => {
    setStep('client');
    setClientId(null);
    setMatterId(null);
    setLineItems([{ description: '', amount: '', quantity: '1' }]);
    setSelectedExpenseIds([]);
    setShowRatesPicker(false);
    setShowDiscardWarning(false);
    setTaxRate('');
    setDiscountRate('');
    setDueDays('30');
    setPaymentTerms('');
    setNotes('');
    setInternalMemo('');
    setIsRecurring(false);
    setRecurringInterval('');
    setBillingAddress({ line1: '', line2: '', city: '', state: '', postal_code: '', country: '' });
  };

  const hasUnsavedChanges = useMemo(() => {
    return !!(
      clientId || matterId || clientName || clientEmail ||
      lineItems.some((li) => li.description || li.amount) ||
      taxRate || discountRate || notes || internalMemo ||
      Object.values(billingAddress).some((v) => v.trim())
    );
  }, [clientId, matterId, clientName, clientEmail, lineItems, taxRate, discountRate, notes, internalMemo, billingAddress]);

  const handleBackdropClick = () => {
    if (hasUnsavedChanges) {
      setShowDiscardWarning(true);
    } else {
      onClose();
      resetForm();
    }
  };

  const confirmDiscard = () => {
    setShowDiscardWarning(false);
    onClose();
    resetForm();
  };

  // ── Memos (must be before early return to keep hook count stable) ──

  const memberOptions = useMemo(() =>
    orgMembers.map(m => ({
      value: m.id,
      label: m.name,
    })),
    [orgMembers]
  );

  const rateOptions = useMemo(() => [
    { value: '', label: t('Custom item') },
    ...billingRates.map((rate: FinanceBillingRate) => ({
      value: String(rate.id),
      label: `${rate.description || rate.rate_type} - ${rate.rate_type === 'contingency' ? `${(rate.amount / 100).toFixed(1)}%` : formatCurrency(rate.amount, rate.currency)}`,
    })),
  ], [billingRates, t]);

  if (!isOpen) return null;

  // ── Step Content ──────────────────────────────────────────

  const renderStepIndicator = () => (
    <div className="glass-bar flex items-center gap-2 px-6 py-3 border-b border-hairline">
      {STEPS.map((s, i) => (
        <React.Fragment key={s}>
          <button
            onClick={() => {
              if (i <= stepIndex || (i === stepIndex + 1 && canGoNext())) setStep(s);
            }}
            className={`nav-pill flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium focus:outline-none focus-visible:ring-2 focus-visible:ring-champagne/60 ${
              s === step
                ? 'nav-pill-active text-fg-strong'
                : i < stepIndex
                ? 'text-success-700 dark:text-success-400'
                : 'text-fg-muted'
            }`}
          >
            <span className={`w-5 h-5 flex items-center justify-center rounded-full text-[10px] font-bold ${
              i < stepIndex ? 'bg-success-500 text-paper' : s === step ? 'bg-champagne/15 border border-champagne/40 text-fg-strong' : 'bg-surface-soft text-fg-muted'
            }`}>
              {i < stepIndex ? <PiCheckLight className="w-3 h-3" /> : i + 1}
            </span>
            {s === 'client' ? t('Client & Matter') : s === 'items' ? t('Line Items') : s === 'adjustments' ? t('Adjustments') : t('Preview')}
          </button>
          {i < STEPS.length - 1 && <div className="w-6 h-px bg-hairline" />}
        </React.Fragment>
      ))}
    </div>
  );

  const renderClientStep = () => (
    <div className="space-y-4">
      <FormSection title={t('Client & Matter')} gridCols={2}>
        <Dropdown
          label={t('Select Client')}
          options={[{ value: '', label: t('No Client (Manual)') }, ...clientOptions]}
          value={clientId ? String(clientId) : ''}
          onChange={(val) => { setClientId(val ? Number(val) : null); setMatterId(null); }}
          placeholder={t('Choose a client...')}
          fullWidth
        />
        <Dropdown
          label={t('Select Matter')}
          options={[{ value: '', label: t('No Specific Matter') }, ...matterOptions]}
          value={matterId ? String(matterId) : ''}
          onChange={(val) => setMatterId(val ? Number(val) : null)}
          placeholder={t('Choose a matter...')}
          fullWidth
        />
        <Input
          label={t('Client Name')}
          type="text"
          value={clientName}
          onChange={(e) => setClientName(e.target.value)}
          placeholder={t('Auto-filled from client')}
        />
        <Input
          label={t('Client Email')}
          type="email"
          value={clientEmail}
          onChange={(e) => setClientEmail(e.target.value)}
          placeholder={t('For sending invoice')}
        />
      </FormSection>

      <FormSection title={t('Billing Address')} gridCols={2}>
        <Input
          type="text"
          placeholder={t('Address Line 1')}
          value={billingAddress.line1}
          onChange={(e) => setBillingAddress((p) => ({ ...p, line1: e.target.value }))}
        />
        <Input
          type="text"
          placeholder={t('Address Line 2')}
          value={billingAddress.line2}
          onChange={(e) => setBillingAddress((p) => ({ ...p, line2: e.target.value }))}
        />
        <Input
          type="text"
          placeholder={t('City')}
          value={billingAddress.city}
          onChange={(e) => setBillingAddress((p) => ({ ...p, city: e.target.value }))}
        />
        <Input
          type="text"
          placeholder={t('State / Region')}
          value={billingAddress.state}
          onChange={(e) => setBillingAddress((p) => ({ ...p, state: e.target.value }))}
        />
        <Input
          type="text"
          placeholder={t('Postal Code')}
          value={billingAddress.postal_code}
          onChange={(e) => setBillingAddress((p) => ({ ...p, postal_code: e.target.value }))}
        />
        <Input
          type="text"
          placeholder={t('Country')}
          value={billingAddress.country}
          onChange={(e) => setBillingAddress((p) => ({ ...p, country: e.target.value }))}
        />
      </FormSection>
    </div>
  );


  const addFromRateById = (rateId: string, index: number) => {
    const rate = billingRates.find((r: FinanceBillingRate) => String(r.id) === rateId);
    if (!rate) return;
    const rateAmount = rate.rate_type === 'contingency'
      ? (rate.amount / 100).toFixed(1)
      : (rate.amount / 100).toFixed(2);
    const updated = [...lineItems];
    updated[index] = {
      ...updated[index],
      description: rate.description || `${rate.rate_type} rate`,
      amount: rateAmount,
      rateId: rate.id,
    };
    setLineItems(updated);
  };

  const renderItemsStep = () => (
    <FormSection
      title={t('Line Items')}
      icon={<PiListLight />}
      noGrid
      actions={unbilledExpenses.length > 0 ? (
        <Button variant="outline" size="small" onClick={() => setShowExpenseImport(!showExpenseImport)}>
          <PiDownloadLight className="w-3.5 h-3.5 me-1" />
          {t('Import Expenses')} ({unbilledExpenses.length})
        </Button>
      ) : undefined}
    >
      {/* Expense Import Panel */}
      {showExpenseImport && (
        <div className="border border-info-200 dark:border-info-800 rounded-lg p-4 bg-info-50/50 dark:bg-info-900/10">
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-xs font-medium text-info-700 dark:text-info-400">{t('Unbilled Expenses')}</h4>
            <Button variant="primary" size="small" onClick={importExpenses} disabled={selectedExpenseIds.length === 0}>
              {t('Import Selected')} ({selectedExpenseIds.length})
            </Button>
          </div>
          <div className="space-y-1.5 max-h-48 overflow-y-auto">
            {unbilledExpenses.map((exp: FinanceExpense) => (
              <div
                key={exp.id}
                className="flex items-center gap-3 p-2 rounded-md hover:bg-info-100/50 dark:hover:bg-info-900/20"
              >
                <CheckBox
                  id={`expense-${exp.id}`}
                  label={`${t(EXPENSE_CATEGORIES[exp.category] || exp.category)}: ${exp.description || '—'}`}
                  translate={false}
                  checked={selectedExpenseIds.includes(exp.id)}
                  onChange={() => toggleExpense(exp.id)}
                  className="flex-1 min-w-0"
                />
                <span className="text-sm font-medium text-fg-strong">
                  {formatCurrency(exp.amount, exp.currency)}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Line Item rows */}
      <div className="space-y-3">
        {lineItems.map((item, index) => (
          <Well key={index} className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-xs font-medium text-fg-muted">#{index + 1}</span>
              <button
                type="button"
                onClick={() => removeLineItem(index)}
                aria-label={t('Remove line item')}
                className="p-1 rounded-md text-fg-muted transition-colors hover:text-danger-500 hover:bg-danger-50 dark:hover:bg-danger-900/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-champagne/60 disabled:opacity-40 disabled:cursor-not-allowed"
                disabled={lineItems.length <= 1}
              >
                <PiTrashLight className="w-4 h-4" />
              </button>
            </div>

            {/* Assignee + Service / Rate selector */}
            <div className={`grid gap-3 ${billingRates.length > 0 ? 'grid-cols-2' : 'grid-cols-1'}`}>
              <Dropdown
                label={t('Assignee')}
                options={[{ value: '', label: t('Unassigned') }, ...memberOptions]}
                value={item.assigneeId || ''}
                onChange={(val) => assignTeamMember(index, val as string)}
                placeholder={t('Unassigned')}
                fullWidth
              />
              {billingRates.length > 0 && (
                <Dropdown
                  label={t('Service / Rate')}
                  options={rateOptions}
                  value={item.rateId ? String(item.rateId) : ''}
                  onChange={(val) => {
                    if (val) addFromRateById(val as string, index);
                    else updateLineItem(index, 'rateId' as any, '');
                  }}
                  placeholder={t('Select a service...')}
                  fullWidth
                />
              )}
            </div>

            {/* Description */}
            <Input
              label={t('Description')}
              type="text"
              placeholder={t('Describe the service or item...')}
              value={item.description}
              onChange={(e) => updateLineItem(index, 'description', e.target.value)}
            />

            {/* Amount + Quantity */}
            <div className="grid grid-cols-2 gap-3">
              <Input
                label={t('Rate / Amount')}
                type="number"
                placeholder="0.00"
                value={item.amount}
                onChange={(e) => updateLineItem(index, 'amount', e.target.value)}
              />
              <Input
                label={t('Qty / Hours')}
                type="number"
                placeholder="1"
                value={item.quantity}
                onChange={(e) => updateLineItem(index, 'quantity', e.target.value)}
              />
            </div>

            {/* Line total */}
            {(item.amount && item.quantity) && (
              <div className="text-end text-xs text-fg-muted">
                {t('Line Total')}: <span className="font-medium text-fg-strong">{formatCurrency((parseFloat(item.amount) || 0) * (parseInt(item.quantity) || 1) * 100, currency)}</span>
              </div>
            )}
          </Well>
        ))}
      </div>

      {/* Add item button */}
      <button
        type="button"
        onClick={addLineItem}
        className="w-full py-2.5 border border-dashed border-hairline rounded-lg text-sm text-fg-muted transition-colors hover:border-fg-muted/40 hover:text-fg-body focus:outline-none focus-visible:ring-2 focus-visible:ring-champagne/60 flex items-center justify-center gap-1.5"
      >
        <PiPlusLight className="w-4 h-4" />
        {t('Add Line Item')}
      </button>

      {/* Running total */}
      <div className="flex justify-end pt-3 border-t border-hairline-soft">
        <div className="text-sm font-medium text-fg-strong">
          {t('Subtotal')}: {formatCurrency(subtotal, currency)}
        </div>
      </div>
    </FormSection>
  );

  const renderAdjustmentsStep = () => (
    <div className="space-y-4">
      <FormSection title={t('Adjustments')} gridCols={2}>
        <Input
          label={t('Tax Rate (%)')}
          type="number"
          value={taxRate}
          onChange={(e) => setTaxRate(e.target.value)}
          placeholder="0"
        />
        <Input
          label={t('Discount Rate (%)')}
          type="number"
          value={discountRate}
          onChange={(e) => setDiscountRate(e.target.value)}
          placeholder="0"
        />
        <Input
          label={t('Due In (days)')}
          type="number"
          value={dueDays}
          onChange={(e) => setDueDays(e.target.value)}
        />
        <Input
          label={t('Payment Terms')}
          type="text"
          value={paymentTerms}
          onChange={(e) => setPaymentTerms(e.target.value)}
          placeholder={t('e.g., Net 30')}
        />
        <Dropdown
          label={t('Currency')}
          options={[
            { value: 'usd', label: 'USD' },
            { value: 'eur', label: 'EUR' },
            { value: 'gbp', label: 'GBP' },
            { value: 'nok', label: 'NOK' },
            { value: 'sek', label: 'SEK' },
          ]}
          value={currency}
          onChange={(val) => setCurrency(val as string)}
          fullWidth
        />
        <div className="flex flex-col gap-3">
          <div className="flex items-center gap-3">
            <Toggle enabled={isRecurring} onToggle={setIsRecurring} />
            <span className="text-sm text-fg-body">{t('Recurring Invoice')}</span>
          </div>
          {isRecurring && (
            <Dropdown
              options={[
                { value: 'weekly', label: t('Weekly') },
                { value: 'monthly', label: t('Monthly') },
                { value: 'quarterly', label: t('Quarterly') },
                { value: 'yearly', label: t('Yearly') },
              ]}
              value={recurringInterval}
              onChange={(val) => setRecurringInterval(val as string)}
              placeholder={t('Interval')}
              compact
            />
          )}
        </div>
      </FormSection>

      <FormSection title={t('Notes')} noGrid>
        <Textarea
          id="invoice-notes"
          label={t('Client-Facing Notes')}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder={t('Notes visible on the invoice...')}
          rows={2}
        />

        <Textarea
          id="invoice-memo"
          label={t('Internal Memo')}
          value={internalMemo}
          onChange={(e) => setInternalMemo(e.target.value)}
          placeholder={t('Internal notes (not visible to client)...')}
          rows={2}
        />
      </FormSection>
    </div>
  );

  const renderPreviewStep = () => {
    const validItems = lineItems.filter((li) => li.description && li.amount);
    return (
      <div className="space-y-6">
        {/* Invoice preview card */}
        <div className="glass rounded-lg p-6">
          {/* Header */}
          <div className="flex justify-between items-start mb-6">
            <div>
              <h3 className="text-lg font-semibold text-fg-strong">{t('Invoice Preview')}</h3>
              <p className="text-xs text-fg-muted mt-1">{t('Draft — Not yet sent')}</p>
            </div>
            <Badge color="gray">{t('Draft')}</Badge>
          </div>

          {/* Client info */}
          {(clientName || clientEmail) && (
            <div className="mb-6">
              <p className="text-xs font-medium text-fg-muted uppercase tracking-wider mb-1">{t('Bill To')}</p>
              <p className="text-sm font-medium text-fg-strong">{clientName || '—'}</p>
              {clientEmail && <p className="text-sm text-fg-body">{clientEmail}</p>}
              {billingAddress.line1 && (
                <div className="text-sm text-fg-body mt-1">
                  <p>{billingAddress.line1}</p>
                  {billingAddress.line2 && <p>{billingAddress.line2}</p>}
                  <p>
                    {[billingAddress.city, billingAddress.state, billingAddress.postal_code]
                      .filter(Boolean).join(', ')}
                  </p>
                  {billingAddress.country && <p>{billingAddress.country}</p>}
                </div>
              )}
            </div>
          )}

          {/* Line items table - full width, no rounded corners */}
          <div className="border-y border-hairline overflow-hidden -mx-6 mb-4">
            <table className="w-full text-sm">
              <thead className="bg-surface-sunken/70">
                <tr>
                  <th className="text-start px-6 py-2 text-xs font-medium text-fg-muted">{t('Description')}</th>
                  <th className="text-start px-4 py-2 text-xs font-medium text-fg-muted">{t('Assignee')}</th>
                  <th className="text-end px-4 py-2 text-xs font-medium text-fg-muted">{t('Qty')}</th>
                  <th className="text-end px-4 py-2 text-xs font-medium text-fg-muted">{t('Rate')}</th>
                  <th className="text-end px-6 py-2 text-xs font-medium text-fg-muted">{t('Amount')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-hairline-soft">
                {validItems.map((item, i) => {
                  const amt = parseFloat(item.amount) || 0;
                  const qty = parseInt(item.quantity) || 1;
                  return (
                    <tr key={i}>
                      <td className="px-6 py-2 text-fg-strong">{item.description}</td>
                      <td className="px-4 py-2 text-fg-body">
                        {item.assigneeName ? (
                          <span className="inline-flex items-center gap-1 text-xs">
                            <PiUserLight className="w-3 h-3" />
                            {item.assigneeName}
                          </span>
                        ) : '—'}
                      </td>
                      <td className="px-4 py-2 text-end text-fg-body">{qty}</td>
                      <td className="px-4 py-2 text-end text-fg-body">{formatCurrency(amt * 100, currency)}</td>
                      <td className="px-6 py-2 text-end font-medium text-fg-strong">{formatCurrency(amt * qty * 100, currency)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Totals */}
          <div className="flex justify-end">
            <div className="w-64 space-y-1">
              <div className="flex justify-between text-sm">
                <span className="text-fg-muted">{t('Subtotal')}</span>
                <span className="font-normal text-fg-strong">{formatCurrency(subtotal, currency)}</span>
              </div>
              {taxAmount > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-fg-muted">{t('Tax')} ({taxRate}%)</span>
                  <span className="font-normal text-fg-strong">{formatCurrency(taxAmount, currency)}</span>
                </div>
              )}
              {discountAmount > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-fg-muted">{t('Discount')} ({discountRate}%)</span>
                  <span className="font-normal text-success-600 dark:text-success-400">-{formatCurrency(discountAmount, currency)}</span>
                </div>
              )}
              <div className="flex justify-between text-sm font-semibold pt-2 border-t border-hairline">
                <span className="text-fg-strong">{t('Total')}</span>
                <span className="text-fg-strong">{formatCurrency(totalAmount, currency)}</span>
              </div>
            </div>
          </div>

          {/* Meta - badges */}
          <div className="mt-6 flex flex-wrap items-center gap-2">
            <Badge color="gray">{t('Due')}: {dueDays} {t('days')}</Badge>
            <Badge color="gray">{currency.toUpperCase()}</Badge>
            {paymentTerms && <Badge color="blue">{paymentTerms}</Badge>}
            {isRecurring && recurringInterval && (
              <Badge color="purple">{t('Recurring')}: {recurringInterval}</Badge>
            )}
            {taxRate && parseFloat(taxRate) > 0 && (
              <Badge color="gray">{t('Tax')}: {taxRate}%</Badge>
            )}
            {discountRate && parseFloat(discountRate) > 0 && (
              <Badge color="green">{t('Discount')}: {discountRate}%</Badge>
            )}
          </div>

          {notes && (
            <Well className="mt-4">
              <p className="text-xs font-medium text-fg-muted mb-1">{t('Notes')}</p>
              <p className="text-sm text-fg-body">{notes}</p>
            </Well>
          )}
        </div>

        {selectedExpenseIds.length > 0 && (
          <div className="text-xs text-fg-muted">
            {t('{{count}} expenses will be marked as invoiced', { count: selectedExpenseIds.length })}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="fixed inset-0 z-[9999999] flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-ink/20 backdrop-blur-[2px]" onClick={handleBackdropClick} />

      {/* Modal */}
      <div className="glass relative w-full max-w-4xl max-h-[90vh] rounded-lg shadow-sm flex flex-col overflow-hidden animate-fade-in">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-hairline">
          <div>
            <h2 className="text-lg font-semibold text-fg-strong">{isEdit ? t('Edit Invoice') : t('Create Invoice')}</h2>
            <p className="text-xs text-fg-muted mt-0.5">
              {isEdit ? t('Update your invoice details') : t('Build a professional invoice step by step')}
            </p>
          </div>
          <button
            onClick={handleBackdropClick}
            aria-label={t('Close')}
            className="p-1.5 rounded-md text-fg-muted transition-colors hover:bg-surface-soft hover:text-fg-strong focus:outline-none focus-visible:ring-2 focus-visible:ring-champagne/60"
          >
            <PiXLight className="w-5 h-5" />
          </button>
        </div>

        {/* Step indicator */}
        {renderStepIndicator()}

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-5 bg-surface-sunken/50">
          {step === 'client' && renderClientStep()}
          {step === 'items' && renderItemsStep()}
          {step === 'adjustments' && renderAdjustmentsStep()}
          {step === 'preview' && renderPreviewStep()}
        </div>

        {/* Footer */}
        <div className="px-6 pb-4">
          <FormActions
            divider
            leading={stepIndex > 0 ? (
              <Button variant="outline" size="small" onClick={goBack}>
                <PiArrowLeftLight className="w-4 h-4 me-1 rtl:-scale-x-100" />
                {t('Back')}
              </Button>
            ) : undefined}
          >
            <Button variant="outline" size="small" onClick={handleBackdropClick}>
              {t('Cancel')}
            </Button>
            {step === 'preview' ? (
              <Button
                variant="primary"
                size="small"
                onClick={handleSubmit}
                isLoading={isEdit ? loading?.updateInvoice : loading?.createInvoice}
              >
                <PiReceiptLight className="w-4 h-4 me-1" />
                {isEdit ? t('Update Invoice') : t('Create Invoice')}
              </Button>
            ) : (
              <Button variant="primary" size="small" onClick={goNext} disabled={!canGoNext()}>
                {t('Next')}
                <PiArrowRightLight className="w-4 h-4 ms-1 rtl:-scale-x-100" />
              </Button>
            )}
          </FormActions>
        </div>

        {/* Discard Warning Overlay */}
        {showDiscardWarning && (
          <div className="absolute inset-0 z-10 flex items-center justify-center">
            <div className="absolute inset-0 bg-ink/20 backdrop-blur-[2px] rounded-lg" onClick={() => setShowDiscardWarning(false)} />
            <div className="glass relative rounded-lg shadow-sm p-6 max-w-sm mx-auto text-center space-y-4">
              <div className="mx-auto w-10 h-10 rounded-full bg-warning-50 dark:bg-warning-500/10 flex items-center justify-center">
                <PiWarningLight className="w-5 h-5 text-warning-600 dark:text-warning-400" />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-fg-strong">{t('Unsaved Changes')}</h3>
                <p className="text-xs text-fg-muted mt-1">{t('You have unsaved changes. Are you sure you want to discard them?')}</p>
              </div>
              <div className="flex gap-3 justify-center">
                <Button variant="outline" size="small" onClick={() => setShowDiscardWarning(false)}>
                  {t('Keep Editing')}
                </Button>
                <Button variant="primary" size="small" onClick={confirmDiscard}>
                  {t('Discard')}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CreateInvoiceModal;
